/**
 * TwoTouchGate — DraftKings Two-Touch Compliance modal.
 *
 * WHY THIS EXISTS
 * ---------------
 * DK's game-integrity policy requires that mass-produced lineups reflect
 * substantive human input. Silently injecting a fixed override (e.g. 99%
 * exposure on the top-2 projected players) produces mathematically
 * identical fingerprints across every user's first run — exactly what
 * DK's automated scanners flag. Prior versions of Sunday Auto Mode + the
 * Portfolio Split banner did that. This gate replaces the trick with two
 * genuine, per-user choices so every fingerprint is different AND the
 * spirit of the rule is honored.
 *
 * TWO REAL TOUCHES
 * ----------------
 * Touch 1 — "Pick a value sleeper" — the user picks 1 of 3 randomly-drawn
 * top-value plays (proj/salary ratio). We write `boost_pct: +15` on their
 * pick. Different users see different candidates because we shuffle the
 * candidate pool, and different picks within a slate write different
 * dk_ids, so no two fingerprints collide.
 *
 * Touch 2 — "Set your ceiling anchor" — the user picks 1 of 3 chalk
 * anchors AND adjusts a max-exposure slider (60-99, defaults 90). We
 * write `max_exposure_pct: <slider>` on their pick. Slider position ×
 * player choice = another axis of variation.
 *
 * Result: 3 × 3 × 40 slider positions = ~360 distinct fingerprints per
 * slate PER preset, before any Pro Mode touches. Every user's portfolio
 * enters DK with a genuinely different footprint.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, Sparkle, Target, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

const SLEEPER_COUNT = 3;
const ANCHOR_COUNT = 3;
const SLIDER_DEFAULT = 90;
const SLIDER_MIN = 60;
const SLIDER_MAX = 99;

// Value = (proj / salary) * 1000. Same formula the Player Pool uses.
// Uses "first positive" fallback because `??` treats real integer zero
// (offseason `market_mean_proj: 0`) as a valid value and locks onto it,
// which incorrectly hides the season-long `avg_ppg` fallback.
const projOf = (p) => {
  for (const v of [p.our_proj, p.projection, p.market_mean_proj, p.avg_ppg]) {
    if (v == null) continue;
    const n = Number(v);
    if (n > 0) return n;
  }
  return 0;
};
const valueOf = (p) => {
  const sal = Number(p.salary || 0);
  const proj = projOf(p);
  return sal > 0 && proj > 0 ? (proj / sal) * 1000 : 0;
};

// Fisher-Yates shuffle for candidate randomization. Seeded with Date.now()
// via Math.random so the candidate slate rotates on every gate open.
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export default function TwoTouchGate({ pool, existingOverrides, onConfirm, onClose }) {
  // Sleepers = mid-salary players ($3.5k-$6.5k, proj > 0) sorted by value.
  // We deliberately exclude the top-2 highest-projected players — those are
  // the "obvious chalk" targets, not sleepers.
  const { sleeperCandidates, anchorCandidates } = useMemo(() => {
    const projected = (pool || []).filter((p) => projOf(p) > 0);
    const alreadyTouched = new Set(Object.keys(existingOverrides || {}));

    // Sleeper pool: mid-salary value plays not already overridden.
    const sleepers = projected
      .filter((p) => {
        const sal = Number(p.salary || 0);
        return sal >= 3500 && sal <= 6500 && !alreadyTouched.has(String(p.dk_id));
      })
      .map((p) => ({ ...p, _value: valueOf(p) }))
      .sort((a, b) => b._value - a._value)
      .slice(0, 15); // top-15 sleepers by value → shuffled → pick 3

    // Anchor pool: top-projected chalk plays not already overridden.
    const anchors = projected
      .filter((p) => !alreadyTouched.has(String(p.dk_id)))
      .sort((a, b) => projOf(b) - projOf(a))
      .slice(0, 12); // top-12 projected → shuffled → pick 3

    return {
      sleeperCandidates: shuffle(sleepers).slice(0, SLEEPER_COUNT),
      anchorCandidates: shuffle(anchors).slice(0, ANCHOR_COUNT),
    };
  }, [pool, existingOverrides]);

  const [sleeperId, setSleeperId] = useState(null);
  const [anchorId, setAnchorId] = useState(null);
  const [ceiling, setCeiling] = useState(SLIDER_DEFAULT);

  // Close on ESC. Standard modal ergonomics.
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const canConfirm = sleeperId && anchorId;
  const sleeperPick = sleeperCandidates.find((p) => String(p.dk_id) === String(sleeperId));
  const anchorPick = anchorCandidates.find((p) => String(p.dk_id) === String(anchorId));

  const confirm = () => {
    if (!canConfirm) return;
    // Merge into existing overrides so we never trample the user's prior touches.
    // If they already have overrides on these players (unlikely — we filtered
    // them out above — but defensive), we preserve those keys and only add ours.
    const next = { ...(existingOverrides || {}) };
    const sKey = String(sleeperId);
    next[sKey] = { ...(next[sKey] || {}), boost_pct: 15 };
    const aKey = String(anchorId);
    next[aKey] = { ...(next[aKey] || {}), max_exposure_pct: Number(ceiling) };
    onConfirm(next, { sleeper: sleeperPick, anchor: anchorPick, ceiling });
  };

  // Sleeper pool empty (offseason before projections publish, or slate
  // < 15 players). We can't offer a genuine gate in that state — bail
  // with a clear message so the caller can decide what to do.
  if (sleeperCandidates.length === 0 || anchorCandidates.length === 0) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        data-testid="two-touch-gate-empty"
        onClick={onClose}
      >
        <div
          className="bg-[#0a0a0a] border-2 border-[#FF8A00] max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-3 mb-3">
            <ShieldCheck size={24} weight="fill" className="text-[#FF8A00] flex-shrink-0" />
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF8A00] font-bold">
                Two-Touch Gate · unavailable
              </div>
              <h3 className="heading text-white font-black text-lg mt-1">Slate not deep enough</h3>
            </div>
          </div>
          <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
            We need at least 3 sleepers ($3.5k-$6.5k) and 3 chalk anchors to offer a genuine
            compliance gate. Load a full DK slate first, or add 2 touches manually in the Player Pool.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-zinc-800 hover:border-[#39FF14] text-zinc-300 hover:text-[#39FF14] text-sm uppercase tracking-widest transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="two-touch-gate"
      onClick={onClose}
    >
      <div
        className="bg-[#0a0a0a] border-2 border-[#39FF14] max-w-2xl w-full my-8"
        style={{ boxShadow: "0 0 40px rgba(57,255,20,0.2)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-800 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <ShieldCheck size={22} weight="fill" className="text-[#39FF14] flex-shrink-0 mt-0.5" />
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.28em] text-[#39FF14] font-bold">
                Make 2 Adjustments · Compliance Check
              </div>
              <h3 className="heading text-white font-black text-xl leading-tight mt-1">
                Two quick touches. Then we ship it.
              </h3>
              <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
                Every optimizer's default output is the same across users — same math, same "best"
                players, same lineups. Make <span className="text-white font-semibold">any 2 adjustments</span> here — pick a sleeper to boost,
                lock or exclude a player, or tweak max exposure. That gives your portfolio a genuine
                per-user fingerprint no other WarPaint account will match.
                <br />
                <span className="text-zinc-500 italic">
                  Example: change 2 max exposures to 99% instead of the default 100%.
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="two-touch-gate-close"
            className="text-zinc-500 hover:text-white transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* TOUCH 1 — Value Sleeper */}
        <div className="px-5 pt-4 pb-3 border-b border-zinc-800">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 flex items-center justify-center border-2 ${
              sleeperId ? "border-[#39FF14] bg-[#39FF14] text-black" : "border-zinc-700 text-zinc-500"
            }`}>
              {sleeperId ? <CheckCircle size={14} weight="fill" /> : <span className="mono text-[11px] font-bold">1</span>}
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-white font-bold">
              Touch 1 · Pick your value sleeper
            </div>
            <Sparkle size={12} weight="fill" className="text-[#FF8A00]" />
          </div>
          <p className="text-[11px] text-zinc-500 mb-3 ml-8">
            Boosts this player's projection by 15%. Different candidates every open — pick who you like.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {sleeperCandidates.map((p) => {
              const active = String(sleeperId) === String(p.dk_id);
              return (
                <button
                  key={p.dk_id}
                  onClick={() => setSleeperId(p.dk_id)}
                  data-testid={`sleeper-${p.dk_id}`}
                  className={`text-left px-3 py-2.5 border-2 transition-colors ${
                    active
                      ? "border-[#39FF14] bg-[#39FF14]/10"
                      : "border-zinc-800 hover:border-[#39FF14]/50 bg-black/40"
                  }`}
                >
                  <div className="text-sm text-white font-semibold truncate">{p.name}</div>
                  <div className="mono text-[10px] text-zinc-500 mt-0.5">
                    {p.position} · {p.team} · ${p.salary}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="mono text-[10px] text-zinc-400">
                      {projOf(p).toFixed(1)} pts
                    </span>
                    <span className={`mono text-[10px] font-bold ${
                      p._value >= 3.0 ? "text-[#39FF14]" : "text-white"
                    }`}>
                      {p._value.toFixed(2)}x
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* TOUCH 2 — Ceiling Anchor */}
        <div className="px-5 pt-4 pb-3 border-b border-zinc-800">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 flex items-center justify-center border-2 ${
              anchorId ? "border-[#39FF14] bg-[#39FF14] text-black" : "border-zinc-700 text-zinc-500"
            }`}>
              {anchorId ? <CheckCircle size={14} weight="fill" /> : <span className="mono text-[11px] font-bold">2</span>}
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-white font-bold">
              Touch 2 · Pick your ceiling anchor
            </div>
            <Target size={12} weight="fill" className="text-[#39FF14]" />
          </div>
          <p className="text-[11px] text-zinc-500 mb-3 ml-8">
            Caps this player's exposure across your lineups. Slider sets the ceiling.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            {anchorCandidates.map((p) => {
              const active = String(anchorId) === String(p.dk_id);
              return (
                <button
                  key={p.dk_id}
                  onClick={() => setAnchorId(p.dk_id)}
                  data-testid={`anchor-${p.dk_id}`}
                  className={`text-left px-3 py-2.5 border-2 transition-colors ${
                    active
                      ? "border-[#39FF14] bg-[#39FF14]/10"
                      : "border-zinc-800 hover:border-[#39FF14]/50 bg-black/40"
                  }`}
                >
                  <div className="text-sm text-white font-semibold truncate">{p.name}</div>
                  <div className="mono text-[10px] text-zinc-500 mt-0.5">
                    {p.position} · {p.team} · ${p.salary}
                  </div>
                  <div className="mono text-[10px] text-zinc-400 mt-1.5">
                    {projOf(p).toFixed(1)} pts
                  </div>
                </button>
              );
            })}
          </div>
          <div className="ml-8">
            <div className="flex items-center justify-between mb-1">
              <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                Max exposure ceiling
              </span>
              <span className="mono text-lg font-black text-[#39FF14]" data-testid="ceiling-value">
                {ceiling}%
              </span>
            </div>
            <input
              type="range"
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              value={ceiling}
              onChange={(e) => setCeiling(Number(e.target.value))}
              data-testid="ceiling-slider"
              className="w-full accent-[#39FF14]"
              style={{ height: 6 }}
            />
            <div className="flex justify-between mono text-[9px] text-zinc-600 mt-1">
              <span>{SLIDER_MIN}%</span>
              <span>90% recommended</span>
              <span>{SLIDER_MAX}%</span>
            </div>
          </div>
        </div>

        {/* Footer / confirm */}
        <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-zinc-500 leading-relaxed flex-1 min-w-0">
            {canConfirm ? (
              <span>
                <CheckCircle size={11} weight="fill" className="inline text-[#39FF14] mr-1 -mt-0.5" />
                Ready: <span className="text-white font-semibold">{sleeperPick?.name}</span> +15% ·{" "}
                <span className="text-white font-semibold">{anchorPick?.name}</span> cap {ceiling}%
              </span>
            ) : (
              <span>Complete both choices to unlock generation.</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              data-testid="two-touch-gate-cancel"
              className="px-4 py-2 border border-zinc-800 hover:border-zinc-600 text-zinc-400 text-xs uppercase tracking-widest transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={!canConfirm}
              data-testid="two-touch-gate-confirm"
              className={`px-5 py-2 uppercase tracking-widest text-xs font-black transition-colors ${
                canConfirm
                  ? "bg-[#39FF14] text-black hover:bg-[#2ee30d]"
                  : "bg-zinc-900 text-zinc-600 cursor-not-allowed"
              }`}
              style={canConfirm ? { boxShadow: "0 0 16px rgba(57,255,20,0.4)" } : {}}
            >
              Apply & Generate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
